/**
 * SEG Hooks
 * Jotai atoms and hooks for SEG management
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { segService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import type { Seg } from '@/types';

// Query atoms
const segByIdQueryAtom = (id: string) =>
  atomWithQuery(() => ({
    queryKey: ['segs', id],
    queryFn: () => segService.getSegById(id),
    enabled: !!id
  }));

// Mutation atoms
const toggleSegStatusMutationAtom = atomWithMutation(() => ({
  mutationKey: ['segs', 'toggle-status'],
  mutationFn: (id: string) => segService.toggleSegStatus(id),
  onSuccess: (_data: unknown, id: string) => {
    invalidateQueries(['segs']);
    invalidateQueries(['segs', id]);
  }
}));

const updateSegMutationAtom = atomWithMutation<Seg, { id: string; data: Record<string, unknown> }>(() => ({
  mutationKey: ['segs', 'update'],
  mutationFn: ({ id, data }) => segService.updateSeg(id, data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['segs']);
    invalidateQueries(['segs', variables.id]);
  }
}));

// Hooks
export function useSegById(id: string) {
  const atom = useMemo(() => segByIdQueryAtom(id), [id]);
  const [query] = useAtom(atom);
  return query;
}

export function useToggleSegStatus() {
  const [mutation] = useAtom(toggleSegStatusMutationAtom);
  return mutation;
}

export function useUpdateSeg() {
  const [mutation] = useAtom(updateSegMutationAtom);
  return mutation;
}
